export const PAGE_SIZE_DEFAULT = 5;
export const PAGE_SIZE_WIDE = 8;
// Batch size for "show more" (accumulating / load-more) lists, as opposed to the
// prev/next paged lists above. The control appears once total > SHOW_MORE_STEP.
export const SHOW_MORE_STEP = 10;

// How many items a "show more" list should actually render, given the running
// visibleCount intent, the current total, and the batch step. Mirrors clampPage:
// it keeps the counter honest when a live list shrinks, and never advertises
// more rows than exist. Pure — no state.
// - Floors to a full step when the list has at least that many (so a stale sub-step
//   count can't hide loaded rows), but never forces the step past a short list.
// - Caps at total; a non-positive total shows nothing.
export function clampShownCount(visibleCount, total, step = SHOW_MORE_STEP) {
  const safeTotal =
    Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  const safeStep = Number.isFinite(step) && step > 0 ? Math.floor(step) : 1;
  const desired = Number.isFinite(visibleCount)
    ? Math.floor(visibleCount)
    : safeStep;
  const floor = Math.min(safeStep, safeTotal);
  const bounded = Math.max(desired, floor);
  return Math.min(bounded, safeTotal);
}

export function getPageCount(total, pageSize) {
  if (!pageSize || pageSize <= 0) return 1;
  return Math.max(1, Math.ceil(total / pageSize));
}

export function clampPage(page, pageCount) {
  const safePageCount = pageCount >= 1 ? pageCount : 1;
  if (!Number.isFinite(page) || page < 1) return 1;
  if (page > safePageCount) return safePageCount;
  return Math.floor(page);
}

export function getPageSlice(items, page, pageSize) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return [];
  // An invalid page size means "no pagination" — show everything (consistent
  // with getPageCount/getPageRange, which both collapse to a single full page).
  if (!pageSize || pageSize <= 0) return list;
  const pageCount = getPageCount(list.length, pageSize);
  const safePage = clampPage(page, pageCount);
  const start = (safePage - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function getPageRange(page, pageSize, total) {
  if (!total || total <= 0) return { start: 0, end: 0 };
  if (!pageSize || pageSize <= 0) return { start: 1, end: total };
  const pageCount = getPageCount(total, pageSize);
  const safePage = clampPage(page, pageCount);
  const start = (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);
  return { start, end };
}
